/**
 * Profile structure: per-user profile page with composable view/edit modules
 * and standalone widget slots.
 *
 * Each profile section is a `ProfileModule` — a view component, optional edit
 * component, optional error checker, and optional preview builder — registered
 * via the `profileModules` config slot. Owners switch individual sections into
 * edit mode inline; saves go through the server `profile/update` API. Simpler
 * non-editable widgets go in `profileWidgets`. Profile pages are marked
 * `noindex` for search engines.
 */

import type { Settings } from '@repo/shared/orpc/procedures/settings';
import { MANAGE_PROFILE_DETAILS_CAPABILITY } from '@repo/shared/roles-capabilities';
import { Container, HorizBox, Pad, PadBox, WindowTitle } from 'component/basics';
import { CTAButton, TextButton } from 'component/button';
import { HeadWhenOnPage } from 'component/navigation/head-when-on-page';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useTranslation } from 'component/translation';
import { useProfile } from 'data/profile';
import { updateProfileAsync } from 'data/profiles';
import React, { useState } from 'react';
import { View } from 'react-native';
import { StandaloneLayout } from 'structure/question/standalone-layout';
import { Catcher } from 'system/catcher';
import { showApiErrorToast } from 'system/orpc-errors';
import { useHasCapability } from 'util/admin';
import { trackEvent } from 'util/analytics';
import type { Datastore } from 'util/datastore';
import { useDatastore, useInstanceKey, usePersonaKey } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import type { StandardConfig } from 'util/stdconfig';
import type { Structure, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { EditProfile } from './profile/edit';
import { ProfileNameOverride } from './profile/name-override';
import { ProfilePhotoOverride } from './profile/photo-override';

export type ProfileModule = {
    label: TLabel;
    resolveLabel?: (config: ProfileConfig) => TLabel;
    editLabel?: TLabel;
    key: string;
    view: React.ComponentType;
    edit?: React.ComponentType;
    mapSaveError?: (error: unknown) => Record<string, boolean> | null;
    /**
     * Optional gate so a module's whole section is dropped, rather than
     * rendered empty, when a silo turns it off. Evaluated against the
     * assembled config by `Profile`.
     */
    isVisible?: (config: ProfileConfig) => boolean;
};

export type CustomNameValidation = {
    errorMessage: TLabel;
};

export const defaultCustomNameValidation: CustomNameValidation = {
    errorMessage: t('profile.error_name_invalid', 'Custom names can only include lowercase letters and numbers.'),
};

export type ProfileEditSidebarWidget = {
    label: TLabel;
    Widget: React.ComponentType;
    /**
     * Optional gate so a widget's whole titled section is dropped, rather than
     * rendered empty, when it shouldn't show. Evaluated against silo settings
     * by `EditProfileSidebar`.
     */
    isVisible?: (settings: Settings | undefined) => boolean;
};

export type ProfileConfig = {
    profileWidgets: React.ComponentType[];
    profileModules: ProfileModule[];
    customNameValidation: CustomNameValidation;
    profileEditSidebarWidgets: ProfileEditSidebarWidget[];
    canEditProfilePhoto?: boolean;
    showDiscussionHistory?: boolean;
};

export const ProfileStructure: Structure<StandardConfig & ProfileConfig> = {
    key: 'profile',
    name: 'User Profile',
    screen: ProfileScreen,
    subscreens: {
        edit: EditProfile,
    },
    receivesConfigDefaults: {
        profileWidgets: [],
        profileEditSidebarWidgets: [
            { label: t('profile.edit.name', 'Profile name'), Widget: ProfileNameOverride },
            { label: t('profile.edit.photo', 'Profile photo'), Widget: ProfilePhotoOverride },
        ],
        profileModules: [],
        customNameValidation: defaultCustomNameValidation,
        canEditProfilePhoto: true,
        showDiscussionHistory: true,
    },
};

export function ProfileMetaData() {
    return (
        <HeadWhenOnPage>
            <meta content='noindex' name='robots' />
        </HeadWhenOnPage>
    );
}

function ProfileScreen() {
    const userId = useInstanceKey();
    const { profile, isLoading } = useProfile(userId);
    const userName = profile?.name;

    // `profile` is null both while the read is in flight and when the key names
    // no real user, so the spinner has to cover `isLoading` explicitly —
    // otherwise every profile page flashes "No such user" before it resolves.
    if (isLoading) return <LoadingSpinnerContainer label={t('profile.loading', 'Loading profile...')} />;
    if (!userName) return <LoadingSpinnerContainer label={t('profile.no_user_error', 'No such user')} />;

    return (
        <StandaloneLayout>
            <WindowTitle text={userName} />
            <ProfileMetaData />
            <Pad />
            <Profile withAdminEditShortcut />
        </StandaloneLayout>
    );
}

export function Profile({ withAdminEditShortcut = false }: { withAdminEditShortcut?: boolean }) {
    const config = useConfig<ProfileConfig>();
    const { profileWidgets, profileModules } = config;
    const canManageProfileDetails = useHasCapability(MANAGE_PROFILE_DETAILS_CAPABILITY);
    const datastore = useDatastore();
    const visibleModules = profileModules.filter(({ isVisible }) => (isVisible ? isVisible(config) : true));
    return (
        <PadBox bottom={40} horiz={20}>
            {visibleModules.map((module) => (
                <Catcher key={module.key}>
                    <ProfileModuleHolder module={module} />
                    <Pad size={20} />
                </Catcher>
            ))}
            {profileWidgets.map((Widget, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <Widget />
                </Catcher>
            ))}
            {/**
            Why do we have this CTAButton here:

            It exists because when we introduced the /edit subscreen,
            we moved all admin-led profile editing to that central page.
            This is mostly fine since admins can just find and edit each
            other through the editorial dashboard's admin table.

            However, the team at Radio-Canada took advantage of the channels
            functionality to create "ghostwriters."

            This wasn't really the intended use case, but they now rely on it.
            Since these ghostwriters aren't real users with admin emails,
            they don't show up in the admin table, making it impossible
            for the editorial team to edit their titles.

            This button is a simple temporary workaround so the team can
            still manage those titles on channel profiles.

            TLDR: This is a band-aid.
            We should remove it (and its related props) once we have
            a proper ghostwriter functionality
            that supports the title writer feature.*/}
            {canManageProfileDetails && withAdminEditShortcut && (
                <>
                    <Pad size={16} />
                    <CTAButton
                        label={t('profile.go_to.edit', 'Edit overrides')}
                        onPress={() => datastore.pushSubscreen('edit')}
                        type='secondary'
                    />
                </>
            )}
        </PadBox>
    );
}

type FieldEditContextType = {
    updates: Record<string, unknown>;
    updateField: (key: string, value: unknown) => void;
    errors: Record<string, boolean>;
};
const FieldEditContext = React.createContext<FieldEditContextType>({
    updates: {},
    updateField: () => {},
    errors: {},
});

export type ProfileModuleHolderProps = { module: Record<string, any> };

export function emptyCustomNameError(updates: { customName?: unknown }): Record<string, boolean> | null {
    return updates.customName === '' ? { nameEmpty: true } : null;
}

async function handleProfileSaveAsync({
    module,
    datastore,
    updates,
    setInProgress,
    setEditing,
    setErrors,
}: {
    module: Record<string, any>;
    datastore: Datastore;
    updates: Record<string, unknown>;
    setInProgress: (v: boolean) => void;
    setEditing: (v: boolean) => void;
    setErrors: (v: Record<string, boolean>) => void;
}) {
    setInProgress(true);
    // This is also checked server-side, but we can save a round-trip
    // by checking it here first and showing the user an inline error.
    const emptyNameError = emptyCustomNameError(updates);
    if (emptyNameError) {
        setErrors(emptyNameError);
        setInProgress(false);
        return;
    }
    try {
        await updateProfileAsync(datastore, updates as Parameters<typeof updateProfileAsync>[1]);
    } catch (error) {
        const saveErrors = module.mapSaveError?.(error);
        if (saveErrors) {
            setErrors(saveErrors);
            setInProgress(false);
            return;
        }
        showApiErrorToast({ datastore, error });
        setInProgress(false);
        return;
    }
    setInProgress(false);
    setEditing(false);
    setErrors({});
    trackEvent(datastore, 'profile-edit', {});
}

function ProfileModuleEditView({
    module,
    updates,
    setUpdates,
    errors,
    inProgress,
    onSave,
    onCancel,
}: {
    module: Record<string, any>;
    updates: Record<string, unknown>;
    setUpdates: (v: Record<string, unknown>) => void;
    errors: Record<string, boolean>;
    inProgress: boolean;
    onSave: () => void;
    onCancel: () => void;
}) {
    return (
        <Container>
            <WithEditableFields errors={errors} setUpdates={setUpdates} updates={updates}>
                <PadBox horiz={20} vert={20}>
                    {React.createElement(module.edit, { updates, setUpdates, errors })}
                    <HorizBox center>
                        <View aria-live='polite'>
                            {inProgress ? (
                                <CTAButton disabled label={t('saving', 'Saving...')} />
                            ) : (
                                <CTAButton label={t('form.save_button', 'Save')} onPress={onSave} />
                            )}
                        </View>
                        <Pad />
                        <TextButton label={t('form.cancel_button', 'Cancel')} onPress={onCancel} />
                    </HorizBox>
                </PadBox>
            </WithEditableFields>
        </Container>
    );
}

function ProfileModuleViewMode({
    module,
    updates,
    setUpdates,
    errors,
    isMyProfile,
    tLabel,
    onEdit,
}: {
    module: Record<string, any>;
    updates: Record<string, unknown>;
    setUpdates: (v: Record<string, unknown>) => void;
    errors: Record<string, boolean>;
    isMyProfile: boolean;
    tLabel: string | null;
    onEdit: () => void;
}) {
    return (
        <View>
            <WithEditableFields errors={errors} setUpdates={setUpdates} updates={updates}>
                {React.createElement(module.view)}
                {isMyProfile && module.edit && (
                    <>
                        <Pad size={20} />
                        <CTAButton
                            formatParams={{ tLabel }}
                            label={module.editLabel ?? t('profile.edit_module_button', 'Edit {tLabel}')}
                            onPress={onEdit}
                            size='small'
                            type='secondary'
                        />
                    </>
                )}
            </WithEditableFields>
        </View>
    );
}

export function ProfileModuleHolder({ module }: ProfileModuleHolderProps) {
    const [editing, setEditing] = useState(false);
    const personaKey = usePersonaKey();
    const instanceKey = useInstanceKey();
    const isMyProfile = personaKey === instanceKey;
    const config = useConfig<ProfileConfig>();
    const effectiveLabel = module.resolveLabel ? module.resolveLabel(config) : module.label;
    const tLabel = useTranslation(effectiveLabel);
    const [updates, setUpdates] = useState<Record<string, unknown>>({});
    const [errors, setErrors] = useState({});
    const [inProgress, setInProgress] = useState(false);
    const datastore = useDatastore();

    async function onSave() {
        await handleProfileSaveAsync({
            module,
            datastore,
            updates,
            setInProgress,
            setEditing,
            setErrors,
        });
    }

    function onCancel() {
        setEditing(false);
        setErrors({});
        setUpdates({});
    }

    if (editing) {
        return (
            <ProfileModuleEditView
                errors={errors}
                inProgress={inProgress}
                module={module}
                onCancel={onCancel}
                onSave={onSave}
                setUpdates={setUpdates}
                updates={updates}
            />
        );
    } else {
        return (
            <ProfileModuleViewMode
                errors={errors}
                isMyProfile={isMyProfile}
                module={module}
                onEdit={() => setEditing(true)}
                setUpdates={setUpdates}
                tLabel={tLabel}
                updates={updates}
            />
        );
    }
}

export type WithEditableFieldsProps = { children: React.ReactNode; updates: any; setUpdates: any; errors: Record<string, boolean> };

export function WithEditableFields({ children, updates, setUpdates, errors }: WithEditableFieldsProps) {
    // HACK
    const pendingUpdatesRef = React.useRef<Record<string, any>>({});
    const updatesRef = React.useRef(updates);
    updatesRef.current = updates;
    const updateField = React.useCallback(
        (key: string, value: any) => {
            pendingUpdatesRef.current = { ...pendingUpdatesRef.current, [key]: value };
            setUpdates({ ...updatesRef.current, ...pendingUpdatesRef.current });
        },
        [setUpdates],
    );

    const contextValue = React.useMemo(() => ({ updates, updateField, errors }), [updates, updateField, errors]);

    return <FieldEditContext.Provider value={contextValue}>{children}</FieldEditContext.Provider>;
}

export function useEditableField(key: string, defaultValue: any): [any, (value: any) => void] {
    const { updates, updateField } = React.useContext(FieldEditContext);
    if (!updates) throw new Error('useEditableField must be used within a WithEditableFields');
    const value = updates?.[key];
    return [value === undefined ? defaultValue : value, (value: any) => updateField(key, value)];
}

export function useEditErrors() {
    const { errors } = React.useContext(FieldEditContext);
    return errors;
}
